import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { productsByCategory, heroImages, categories } from "../../data/productData";

// Product details are shown on a dedicated page instead of a modal

// Animated Product Card Component
const ProductCard = ({ product, index, onProductClick }) => {
  return (
    <div
      className="group product-card bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
      style={{ animationDelay: `${index * 100}ms` }}
      onClick={() => onProductClick(product)}>
      <div className="relative h-48 overflow-hidden">
        <img
          src={product.image || "/assets/productimages/placeholder.jpg"}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            e.target.src = "/assets/productimages/placeholder.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Hover Overlay */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0,163,161,0.12)" }}>
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 transform scale-0 group-hover:scale-100 transition-transform duration-300">
            <svg
              className="w-6 h-6 icon-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 card-title transition-colors duration-200">
          {product.name}
        </h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.use}</p>
        <Link to="/contact-us">
          <button className="w-full py-2 px-4 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 btn-accent">
            Get Quote
          </button>
        </Link>
      </div>
    </div>
  );
};

// Animated Category Tabs Component
const CategoryTabs = ({ categories, activeTab, onTabChange }) => {
  // Exclude metadata keys (e.g. description) which are not actual tabs
  const tabKeys = Object.keys(categories || {}).filter(
    (k) => typeof categories[k] === "object" && categories[k] !== null
  );

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 animate-slide-in-left">
      <div className="flex flex-wrap gap-3 justify-center">
        {tabKeys.map((category, index) => (
          <button
            key={category}
            onClick={() => onTabChange(category)}
            className={`relative px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
              activeTab === category
                ? "btn-accent text-white shadow-lg"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md"
            }`}
            style={{ animationDelay: `${index * 100}ms` }}>
            {category}
            {activeTab === category && (
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full animate-bounce bg-accent" />
            )}

            <div
              className={`absolute inset-0 rounded-xl transition-all duration-300 opacity-0 group-hover:opacity-20`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// Search Component
const SearchBar = ({ searchTerm, onSearchChange }) => {
  return (
    <div className="max-w-md mx-auto mb-8 animate-slide-in-right">
      <div className="relative group">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full px-6 py-4 text-lg border-2 border-gray-200 rounded-2xl shadow-lg focus:outline-none transition-all duration-300 group-hover:shadow-xl"
        />
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
          <svg
            className="w-6 h-6 text-gray-400 search-icon transition-colors duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Animated search icon */}
        {searchTerm && (
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 animate-bounce">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "var(--accent)" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Hero Section (uses same hero image as categories page)
const HeroSection = ({ categoryName, image }) => {
  const bg = image || heroImages[1];
  return (
    <div className="relative mb-12 overflow-hidden w-full">
      <div
        className="w-full h-64 md:h-[470px] bg-center bg-cover"
        style={{ backgroundImage: `url(${bg})` }}
      />
      <div className="absolute inset-0 bg-black/40 flex items-center">
        <div className="max-w-5xl px-6 lg:px-12 text-left text-white">
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">{categoryName}</h1>
          <p className="mt-3 max-w-2xl text-md md:text-xl font-light">
            Discover our comprehensive range of high-quality products designed to meet all your construction and
            renovation needs.
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Component
const ProductSubcategories = () => {
  const { id, sub } = useParams();
  const categoryData = productsByCategory[id];
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  // choose first real tab (exclude metadata such as 'description')
  const firstTab = (() => {
    const keys = Object.keys(categoryData || {}).filter(
      (k) => typeof categoryData[k] === "object" && categoryData[k] !== null
    );
    return keys[0] || "";
  })();

  const [activeTab, setActiveTab] = useState(firstTab || "");
  const [activeOption, setActiveOption] = useState("");
  const [activeNestedOption, setActiveNestedOption] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'

  // Format category name from URL ID
  const formattedCategoryName = id
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  // find friendly category metadata (shows proper display name and image)
  const categoryMeta = categories.find((c) => c.link === id) || {};

  // Breadcrumb component (local, used only in new/modified components)
  const Breadcrumbs = ({ parts }) => (
    <nav
      className="breadcrumbs"
      aria-label="Breadcrumb">
      <ol className="flex items-center">
        <li>
          <Link
            to="/"
            aria-label="Home"
            className="hover:underline">
            Home
          </Link>
        </li>
        <li aria-hidden>
          <svg
            className="w-4 h-4 text-gray-400 mx-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </li>
        <li>
          <Link
            to="/our-products"
            className="hover:underline">
            Products
          </Link>
        </li>
        {parts &&
          parts.map((p, i) => (
            <li
              key={i}
              className="flex items-center">
              <svg
                className="w-4 h-4 text-gray-400 mx-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              {p.to && i === parts.length - 1 ? (
                <Link
                  to={p.to}
                  className="crumb-current">
                  {p.label}
                </Link>
              ) : (
                <span className="crumb-current">{p.label}</span>
              )}
            </li>
          ))}
      </ol>
    </nav>
  );

  // Filter products for active tab based on search term
  const resolveProducts = () => {
    const entry = categoryData?.[activeTab];
    if (!entry) return [];

    // If tab directly provides an array of products
    if (Array.isArray(entry)) return entry;

    // If tab is an object with .products
    if (entry.products) return entry.products;

    // If tab has options
    if (entry.options) {
      if (!activeOption) return [];
      const opt = entry.options[activeOption];
      if (!opt) return [];
      if (opt.products) return opt.products;
      if (opt.options) {
        if (!activeNestedOption) return [];
        return opt.options[activeNestedOption]?.products || [];
      }
    }

    return [];
  };

  const filteredProducts = resolveProducts().filter(
    (product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.use || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProductClick = (product) => {
    const pid = product.id || encodeURIComponent((product.name || "").replace(/\s+/g, "_"));
    navigate(`/our-products/${id}/${pid}`);
  };

  // Align active tab with optional subcategory slug from URL
  useEffect(() => {
    if (!sub || !categoryData) return;
    const keys = Object.keys(categoryData).filter(
      (k) => typeof categoryData[k] === "object" && categoryData[k] !== null
    );
    const target = sub.replace(/-/g, " ").toLowerCase();
    const match = keys.find((k) => k.toLowerCase() === target);
    if (match && match !== activeTab) setActiveTab(match);
  }, [sub, categoryData]);

  // When tab changes reset option selections
  useEffect(() => {
    setActiveNestedOption("");
    const tabEntry = categoryData?.[activeTab];
    if (tabEntry && tabEntry.options) {
      const firstOption = Object.keys(tabEntry.options)[0] || "";
      setActiveOption(firstOption);
      // if that option has nested options, set its first nested
      const nested = tabEntry.options[firstOption]?.options;
      if (nested) {
        setActiveNestedOption(Object.keys(nested)[0] || "");
      } else {
        setActiveNestedOption("");
      }
    } else {
      setActiveOption("");
    }
  }, [activeTab]);

  if (!categoryData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-accent-vertical">
        <div className="text-center p-8 bg-white rounded-2xl shadow-2xl">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Category Not Found</h1>
          <p className="text-gray-600">The requested category does not exist or has been moved.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-accent-vertical">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumbs (always at the top) */}
        <div className="mb-6">
          <Breadcrumbs
            parts={[
              { label: categoryMeta.name || formattedCategoryName },
              ...(sub
                ? [
                    {
                      label: sub.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                    },
                  ]
                : []),
            ]}
          />
        </div>

        {/* Hero Section (use per-category image when available) */}
        <HeroSection
          categoryName={categoryMeta.name || formattedCategoryName}
          image={categoryMeta.image}
        />

        {/* Search Bar */}
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {/* Category Tabs */}
        <CategoryTabs
          categories={categoryData}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Options (if present) */}
        {(() => {
          const tabEntry = categoryData?.[activeTab];
          if (!tabEntry) return null;

          // If the tab has options, render option buttons
          const options = tabEntry.options;
          if (!options) return null;

          return (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Options</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.keys(options).map((optKey) => (
                  <button
                    key={optKey}
                    onClick={() => {
                      setActiveOption(optKey);
                      // reset nested when option changes
                      setActiveNestedOption("");
                    }}
                    className={`px-3 py-1 rounded-full text-sm ${
                      activeOption === optKey ? "bg-accent text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}>
                    {optKey}
                  </button>
                ))}
              </div>

              {/* If selected option has nested options, render them */}
              {activeOption && options[activeOption] && options[activeOption].options && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-2">Sub-options</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(options[activeOption].options).map((nKey) => (
                      <button
                        key={nKey}
                        onClick={() => setActiveNestedOption(nKey)}
                        className={`px-3 py-1 rounded-full text-sm ${
                          activeNestedOption === nKey
                            ? "bg-accent text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}>
                        {nKey}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Products Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center">
                <span className="w-2 h-8 rounded-full mr-4 bg-accent" />
                {activeTab}
              </h2>

              {/* Product Count Badge */}
              <div
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{ backgroundColor: "var(--accent-50)", color: "var(--accent-dark)" }}>
                {filteredProducts.length} {filteredProducts.length === 1 ? "Product" : "Products"}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === "grid" ? "bg-white text-accent shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === "list" ? "bg-white text-accent shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>

              <Link to={`/our-products/${id}/${id}`}>
                <button className="px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 btn-accent">
                  View More
                </button>
              </Link>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-16">
              <div
                className="inline-flex items-center px-4 py-2 font-semibold leading-6"
                style={{ color: "var(--accent)" }}>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading products...
              </div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg animate-bounce-in">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-10 h-10 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <p className="text-lg text-gray-600">No matching products found.</p>
              <p className="text-sm text-gray-500 mt-2">Try adjusting your search terms.</p>

              {/* Clear search button */}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="mt-4 text-white px-4 py-2 rounded-lg transition-colors duration-200 btn-accent">
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div
              className={
                viewMode === "grid"
                  ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  : "space-y-4"
              }>
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className={`animate-fade-in-up hover:animate-bounce-in ${viewMode === "list" ? "w-full" : ""}`}
                  style={{ animationDelay: `${index * 100}ms` }}>
                  {viewMode === "list" ? (
                    <div
                      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 flex items-center space-x-6 group cursor-pointer list-card"
                      onClick={() => handleProductClick(product)}>
                      <div className="flex-shrink-0">
                        <img
                          src={product.image || "/assets/productimages/placeholder.jpg"}
                          alt={product.name}
                          className="w-24 h-24 object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = "/assets/productimages/placeholder.jpg";
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 list-card-title transition-colors duration-200 mb-2">
                          {product.name}
                        </h3>
                        <p className="text-gray-600 mb-3">{product.use}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            {product.availability}
                          </span>
                          <span className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                              />
                            </svg>
                            {product.price}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Link to="/contact-us">
                          <button className="px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 text-white btn-accent">
                            Get Quote
                          </button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <ProductCard
                      product={product}
                      index={index}
                      onProductClick={handleProductClick}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Product detail now handled on its own page */}

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 btn-accent-circle"
          title="Scroll to top">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      </div>

      {/* Background Decorative Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div
          className="absolute top-20 left-10 w-32 h-32 rounded-full opacity-10 animate-pulse"
          style={{ backgroundColor: "var(--accent-50)" }}
        />
        <div
          className="absolute top-40 right-20 w-24 h-24 rounded-full opacity-10 animate-pulse"
          style={{ animationDelay: "1s", backgroundColor: "var(--comp-50)" }}
        />
        <div
          className="absolute bottom-40 left-20 w-20 h-20 rounded-full opacity-10 animate-pulse"
          style={{ animationDelay: "2s", backgroundColor: "var(--accent-100)" }}
        />
      </div>
    </div>
  );
};

export default ProductSubcategories;

// Add custom CSS animations and small utilities that are local to this file
const styles = `
  @keyframes fade-in-up {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slide-in-left {
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes slide-in-right {
    from { opacity: 0; transform: translateX(30px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes bounce-in {
    0% { opacity: 0; transform: scale(0.3); }
    50% { opacity: 1; transform: scale(1.05); }
    70% { transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
  .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
  .animate-slide-in-left { animation: slide-in-left 0.6s ease-out forwards; }
  .animate-slide-in-right { animation: slide-in-right 0.6s ease-out forwards; }
  .animate-bounce-in { animation: bounce-in 0.8s ease-out forwards; }
  .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .shadow-3xl { box-shadow: 0 35px 60px -12px rgba(0,0,0,0.25); }
  .backdrop-blur-sm { backdrop-filter: blur(4px); }
`;

// Inject animations/styles for this module only
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
